// Import products from Excel/CSV or pasted cells. Everything runs on the
// device: parse (worker), map, review, then commit in chunks through the
// normal outbox, so it works offline and syncs like any other change. Big
// sheets go in parts of 20,000 rows (lib/import/parts.ts), one after another.
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Select,
  TH,
  Textarea,
  cx,
  useToast,
  SkeletonPage,
  SkeletonTable,
} from '@ims/ui'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardPaste,
  Download,
  FileSpreadsheet,
  Upload,
} from '@ims/ui/icons'
import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { LocationSelect, useLookups } from '../components.tsx'
import {
  errorMessage,
  useEntitlements,
  useFieldGroups,
  useLive,
  useSettings,
  useSyncState,
} from '../hooks.ts'
import { attributeFields } from '../lib/catalog.ts'
import { downloadBlob, todayStamp } from '../lib/download.ts'
import { customerPays } from '../lib/bulk-edit.ts'
import { money, qty } from '../lib/format.ts'
import {
  FIELDS,
  attributeFieldDefs,
  fieldLabel,
  autoMap,
  buildPlan,
  detectHeaderRow,
  headerFingerprint,
  notImportedRows,
  partCount,
  partRange,
  productsSaved,
  parseTsv,
  planToChunks,
  PART_ROWS,
  sanitizeMapping,
  templateGuideRows,
  templateRows,
  vatHints,
  tidyRows,
  type MapKey,
  type ImportOptions,
  type ImportPlan,
  type Mapping,
  type PlannedRow,
  type SheetRows,
  type VatHints,
} from '../lib/import/index.ts'
import type { SpreadsheetSheet } from '../platform.ts'
import { useSession } from '../session.tsx'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'

type Step = 'data' | 'map' | 'review' | 'done'
type Filter = 'all' | 'error' | 'create' | 'update' | 'skip'

const PAGE = 50
const CHUNK = 100
const STEPS: { key: Step; label: string }[] = [
  { key: 'data', label: 'Add your data' },
  { key: 'map', label: 'Match columns' },
  { key: 'review', label: 'Review' },
  { key: 'done', label: 'Done' },
]

interface Source {
  fileName: string
  sheets: SpreadsheetSheet[]
}

interface Result {
  created: number
  updated: number
  skipped: number
  errors: number
  overLimit: number
}

/** An import that stopped (Stop pressed, or an error) before its last chunk. */
interface Stopped {
  saved: number
  total: number
  error: string | null
}

function mappingKey(tenantId: string, headers: string[]) {
  return `ims.import.mapping.${tenantId}.${headerFingerprint(headers)}`
}

function rememberedMapping(key: string): Mapping | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as Mapping) : null
  } catch {
    return null
  }
}

export function ImportScreen() {
  const repo = useRepo()
  const toast = useToast()
  const navigate = useNavigate()
  const { platform } = useSession()
  const { current } = useWorkspace()
  const canImport = useCan('staff')
  const canUpdate = useCan('manager')
  const settings = useSettings()
  const lookups = useLookups()
  const fieldGroups = useFieldGroups()
  const shownAttributes = useMemo(
    () => attributeFields(fieldGroups.groups, fieldGroups.custom).flatMap((s) => s.fields),
    [fieldGroups],
  )
  const extraFields = useMemo(() => attributeFieldDefs(shownAttributes), [shownAttributes])
  const allFields = useMemo(() => [...FIELDS, ...extraFields], [extraFields])
  const entitlements = useEntitlements()
  const sync = useSyncState()
  const products = useLive(
    async (r) => (await r.list('products', { limit: 1_000_000 })).rows,
    [],
    ['products'],
  )
  // Products with any stock movement can't become variant groups.
  const stocked = useLive(
    async (r) => new Set((await r.stock.levels()).map((l) => l.product_id)),
    [],
    ['stock_movements'],
  ).data

  const [step, setStep] = useState<Step>('data')
  const [source, setSource] = useState<Source | null>(null)
  const [sheetIndex, setSheetIndex] = useState(0)
  const [headerRow, setHeaderRow] = useState(0)
  const [part, setPart] = useState(0)
  const [mapping, setMapping] = useState<Mapping>({})
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [optionOverrides, setOptionOverrides] = useState<Partial<ImportOptions>>({})
  const [locationOverride, setLocationOverride] = useState<string | null>(null)
  const [skippedCategories, setSkippedCategories] = useState<Set<string>>(new Set())
  const [skippedBrands, setSkippedBrands] = useState<Set<string>>(new Set())
  const [unitChoices, setUnitChoices] = useState<
    Record<string, { create: boolean; allowsDecimal: boolean }>
  >({})
  const [limitToAllowed, setLimitToAllowed] = useState(false)
  const [filter, setFilter] = useState<Filter>('all')
  const [offset, setOffset] = useState(0)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  const [stopped, setStopped] = useState<Stopped | null>(null)
  const stopRequested = useRef(false)
  const [leftOver, setLeftOver] = useState<SheetRows | null>(null)
  const [pasting, setPasting] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)

  const tenantId = current?.tenant_id ?? ''
  const rawRows = useMemo(
    () => tidyRows(source?.sheets[sheetIndex]?.rows ?? []),
    [source, sheetIndex],
  )
  const headers = useMemo(() => rawRows[headerRow] ?? [], [rawRows, headerRow])
  const columnCount = useMemo(
    () =>
      Math.max(headers.length, ...rawRows.slice(headerRow, headerRow + 50).map((r) => r.length)),
    [headers, rawRows, headerRow],
  )
  const totalDataRows = Math.max(0, rawRows.length - headerRow - 1)
  const parts = partCount(totalDataRows)
  const range = partRange(part, totalDataRows)
  const dataRows = useMemo(() => {
    const rows = rawRows.slice(headerRow + 1 + range.start, headerRow + 1 + range.end)
    if (Object.keys(edits).length === 0) return rows
    return rows.map((row, i) => {
      let out = row
      for (let col = 0; col < columnCount; col++) {
        const edit = edits[`${i}:${col}`]
        if (edit !== undefined) {
          if (out === row) out = [...row]
          out[col] = edit
        }
      }
      return out
    })
  }, [rawRows, headerRow, range.start, range.end, edits, columnCount])
  const rowNumbers = useMemo(
    () => dataRows.map((_, i) => headerRow + 2 + range.start + i),
    [dataRows, headerRow, range.start],
  )
  const truncated = source?.sheets[sheetIndex]?.truncated ?? false

  const maxProducts = entitlements?.limits?.max_products ?? null
  const activeCount = (products.data ?? []).length
  const productsRemaining = maxProducts == null ? null : Math.max(0, maxProducts - activeCount)

  const options: ImportOptions = useMemo(
    () => ({
      pricesIncludeVat: settings?.prices_include_tax ?? true,
      defaultTaxRateId: settings?.default_tax_rate_id ?? null,
      onExisting: 'skip',
      ...optionOverrides,
      createLimit: limitToAllowed ? productsRemaining : null,
      newUnits: unitChoices,
    }),
    [settings, optionOverrides, limitToAllowed, productsRemaining, unitChoices],
  )
  const locationId = locationOverride ?? lookups.activeLocations[0]?.id ?? null

  const plan = useMemo(() => {
    if (step !== 'review' && step !== 'done') return null
    if (!products.data || !stocked || lookups.loading) return null
    return buildPlan(dataRows, rowNumbers, mapping, {
      products: products.data,
      units: lookups.units,
      taxRates: lookups.taxRates,
      categories: lookups.categories,
      brands: lookups.brands,
      attributeFields: shownAttributes,
      stockedProductIds: stocked,
      options,
      productsRemaining,
      canCreateUnits: canImport,
    })
  }, [
    step,
    dataRows,
    rowNumbers,
    mapping,
    products.data,
    lookups,
    options,
    productsRemaining,
    canImport,
    shownAttributes,
    stocked,
  ])

  const hints = useMemo(() => (plan ? vatHints(plan) : null), [plan])

  if (!canImport) {
    return (
      <EmptyState
        title="You can’t import products"
        description="Ask the owner or a manager to give you staff access."
      />
    )
  }
  // Column matching depends on the business's fields (settings): wait for them.
  if (!fieldGroups.loaded) return <SkeletonPage />

  // ---------------------------------------------------------------------------
  // Loading data

  const loadSheets = (fileName: string, sheets: SpreadsheetSheet[]) => {
    const withRows = sheets.filter((s) =>
      s.rows.some((r) => r.some((c) => String(c).trim() !== '')),
    )
    if (withRows.length === 0) {
      toast.error('That file has no data')
      return
    }
    setSource({ fileName, sheets: withRows })
    selectSheet(withRows, 0)
  }

  const selectSheet = (sheets: SpreadsheetSheet[], index: number) => {
    const rows = tidyRows(sheets[index]?.rows ?? [])
    setSheetIndex(index)
    selectHeader(rows, detectHeaderRow(rows))
  }

  const selectHeader = (rows: string[][], index: number) => {
    setHeaderRow(index)
    setPart(0)
    setEdits({})
    const hdrs = rows[index] ?? []
    const remembered = tenantId ? rememberedMapping(mappingKey(tenantId, hdrs)) : null
    setMapping(
      sanitizeMapping(
        remembered ?? autoMap(hdrs, rows.slice(index + 1, index + 21), extraFields),
        hdrs.length,
        extraFields,
      ),
    )
  }

  const onFile = async (file: File) => {
    if (file.size > 25 * 1024 * 1024) return toast.error('That file is larger than 25 MB')
    setBusy(true)
    try {
      const sheets = await platform.spreadsheets.read(await file.arrayBuffer())
      loadSheets(file.name, sheets)
    } catch (error) {
      toast.error(`Could not read the file: ${errorMessage(error)}`)
    } finally {
      setBusy(false)
    }
  }

  const applyPasted = () => {
    const rows = parseTsv(pasteText)
    if (rows.length < 2) return toast.error('Paste a header row and at least one product')
    loadSheets('Pasted cells', [{ name: 'Pasted', rows }])
    setPasting(false)
    setPasteText('')
  }

  /**
   * The template is made for this business when it is asked for: the columns of its field
   * groups, examples of its own kind of goods, its units and its VAT habit. `everything`
   * adds the columns of the groups it has switched off.
   */
  const downloadTemplate = async (everything = false) => {
    try {
      const vat = lookups.taxRates.find((t) => t.id === settings?.default_tax_rate_id)
      const options = {
        businessType: settings?.business_type,
        groups: everything ? ('all' as const) : fieldGroups.groups,
        attributeFields: shownAttributes,
        unitSymbols: lookups.units.map((u) => u.symbol),
        vatPercent: vat ? vat.rate_bp / 100 : null,
        pricesIncludeVat: settings?.prices_include_tax ?? true,
      }
      const blob = await platform.spreadsheets.write([
        { name: 'Products', rows: templateRows(options) },
        { name: 'How to fill', rows: templateGuideRows(options) },
      ])
      downloadBlob(
        blob,
        everything
          ? 'sano-godam-products-template-all-columns.xlsx'
          : 'sano-godam-products-template.xlsx',
      )
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  // ---------------------------------------------------------------------------
  // Mapping

  const setColumnField = (col: number, field: MapKey | '') => {
    setMapping((m) => {
      const next: Mapping = {}
      for (const [k, c] of Object.entries(m) as [MapKey, number][]) {
        if (c !== col && k !== field) next[k] = c
      }
      if (field) next[field] = col
      return next
    })
  }
  const missingRequired = FIELDS.filter((f) => f.required && mapping[f.key] === undefined)
  // Only names (and codes) matched: probably a list of categories, suppliers or units.
  const onlyNames =
    missingRequired.length === 0 &&
    Object.keys(mapping).every((k) => k === 'name' || k === 'sku' || k === 'description')

  const toReview = () => {
    try {
      if (tenantId) localStorage.setItem(mappingKey(tenantId, headers), JSON.stringify(mapping))
    } catch {
      // Storage may be unavailable (private mode); remembering is only a convenience.
    }
    setOffset(0)
    setFilter('all')
    setStep('review')
  }

  /** Moves to another part of the sheet, keeping the column matching and choices. */
  const goToPart = (next: number) => {
    setPart(next)
    setEdits({})
    setLimitToAllowed(false)
    setStopped(null)
    setResult(null)
    setOffset(0)
    setFilter('all')
    setStep('review')
  }

  // ---------------------------------------------------------------------------
  // Import

  const runImport = async () => {
    if (!plan || !current) return
    const createCategories = new Set(
      plan.newCategories
        .map((c) => c.trim().toLowerCase())
        .filter((c) => !skippedCategories.has(c)),
    )
    const createBrands = new Set(
      plan.newBrands.map((b) => b.trim().toLowerCase()).filter((b) => !skippedBrands.has(b)),
    )
    const chunks = planToChunks(
      plan,
      { tenantId, locationId, createCategories, createBrands },
      CHUNK,
    )
    const total = plan.counts.create + plan.counts.update
    const overLimit = plan.rows.filter((r) => r.overLimit).length
    stopRequested.current = false
    setStopped(null)
    setBusy(true)
    setProgress({ done: 0, total })
    let done = 0
    try {
      // Each chunk is saved on its own, so stopping (or failing) keeps what's
      // already in; the recomputed plan then only has the rest to import.
      for (; done < chunks.length; done++) {
        if (stopRequested.current) break
        await repo.commit(chunks[done]!)
        setProgress({ done: productsSaved(done + 1, total, CHUNK), total })
      }
      if (done < chunks.length) {
        setStopped({ saved: productsSaved(done, total, CHUNK), total, error: null })
        return
      }
      setResult({
        created: plan.counts.create,
        updated: plan.counts.update,
        skipped: plan.counts.skip - overLimit,
        errors: plan.counts.error,
        overLimit,
      })
      // The review is recomputed once products change; keep the rows left out as they are now.
      setLeftOver(notImportedRows(headers, dataRows, plan))
      setStep('done')
    } catch (error) {
      setStopped({ saved: productsSaved(done, total, CHUNK), total, error: errorMessage(error) })
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  const downloadRows = async (rows: SheetRows) => {
    try {
      const blob = await platform.spreadsheets.write([{ name: 'Not imported', rows }])
      downloadBlob(blob, `import-not-imported-${todayStamp()}.xlsx`)
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const reset = () => {
    setStep('data')
    setSource(null)
    setPart(0)
    setEdits({})
    setResult(null)
    setStopped(null)
    setLeftOver(null)
    setOptionOverrides({})
    setSkippedCategories(new Set())
    setSkippedBrands(new Set())
    setUnitChoices({})
    setLimitToAllowed(false)
  }

  // ---------------------------------------------------------------------------

  return (
    <div>
      <PageHeader
        title="Import products"
        subtitle="From an Excel or CSV file, or cells copied from a spreadsheet."
        actions={
          <Button
            variant="secondary"
            icon={<ArrowLeft className="size-4" />}
            onClick={() => navigate('/products')}
          >
            Products
          </Button>
        }
      />
      <Stepper step={step} />

      {step === 'data' && (
        <div className="space-y-4">
          <Card>
            <div className="grid gap-4 md:grid-cols-3">
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault()
                  const f = e.dataTransfer.files[0]
                  if (f) void onFile(f)
                }}
                className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border p-6 text-center hover:border-primary/50 hover:bg-primary-light/40 md:col-span-2"
              >
                <Upload className="size-7 text-primary" />
                <span className="font-semibold">
                  {busy ? (
                    'Reading…'
                  ) : (
                    <>
                      Choose a file<span className="hidden sm:inline"> or drop it here</span>
                    </>
                  )}
                </span>
                <span className="text-sm text-muted">
                  .xlsx, .xls or .csv. Big lists are imported 20,000 rows at a time.
                </span>
              </button>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,.xls,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                aria-label="Spreadsheet file"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) void onFile(f)
                  e.target.value = ''
                }}
              />
              <div className="flex flex-col gap-2">
                <Button
                  variant="secondary"
                  icon={<ClipboardPaste className="size-4" />}
                  onClick={() => setPasting((p) => !p)}
                >
                  Paste from Excel
                </Button>
                <Button
                  variant="secondary"
                  icon={<Download className="size-4" />}
                  onClick={() => void downloadTemplate()}
                >
                  Download template
                </Button>
                <p className="text-xs text-muted">
                  No sheet yet? Fill in the template: it has the columns your business uses and a
                  few examples.{' '}
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    onClick={() => void downloadTemplate(true)}
                  >
                    Get every column
                  </button>
                </p>
              </div>
            </div>
            {pasting && (
              <div className="mt-4 space-y-2">
                <Field
                  label="Paste cells"
                  hint="Copy the rows (with the header row) in Excel or Google Sheets, then paste here."
                >
                  {(id) => (
                    <Textarea
                      id={id}
                      rows={6}
                      value={pasteText}
                      onChange={(e) => setPasteText(e.target.value)}
                      className="font-mono text-xs"
                    />
                  )}
                </Field>
                <Button onClick={applyPasted} disabled={!pasteText.trim()}>
                  Use pasted cells
                </Button>
              </div>
            )}
          </Card>

          {source && (
            <Card>
              <div className="mb-4 flex flex-wrap items-end gap-4">
                <p className="flex items-center gap-2 font-semibold">
                  <FileSpreadsheet className="size-5 text-primary" />
                  {source.fileName}
                </p>
                {source.sheets.length > 1 && (
                  <Field label="Sheet" className="w-48">
                    {(id) => (
                      <Select
                        id={id}
                        value={sheetIndex}
                        onChange={(e) => selectSheet(source.sheets, Number(e.target.value))}
                      >
                        {source.sheets.map((s, i) => (
                          <option key={s.name} value={i}>
                            {s.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                )}
                <Field label="Header row" className="w-72">
                  {(id) => (
                    <Select
                      id={id}
                      value={headerRow}
                      onChange={(e) => selectHeader(rawRows, Number(e.target.value))}
                    >
                      {rawRows.slice(0, 20).map((r, i) => (
                        <option key={i} value={i}>
                          Row {i + 1}:{' '}
                          {r.filter(Boolean).slice(0, 4).join(', ').slice(0, 40) || '(empty)'}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-xs">
                  <tbody>
                    {rawRows.slice(0, 10).map((r, i) => (
                      <tr
                        key={i}
                        className={cx(
                          'border-b border-border last:border-0',
                          i === headerRow && 'bg-primary-light font-semibold',
                          i < headerRow && 'text-subtle',
                        )}
                      >
                        <td className="w-10 px-2 py-1.5 text-subtle">{i + 1}</td>
                        {Array.from({ length: Math.min(columnCount, 12) }, (_, c) => (
                          <td key={c} className="max-w-40 truncate px-2 py-1.5">
                            {r[c] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parts > 1 && (
                <Banner tone="primary">
                  This sheet has {totalDataRows.toLocaleString('en-IN')} rows. It’s imported in{' '}
                  {parts} parts of up to {PART_ROWS.toLocaleString('en-IN')} rows; when one part
                  finishes, you can go straight on to the next.
                </Banner>
              )}
              {truncated && (
                <Banner tone="warning">
                  Only the first {totalDataRows.toLocaleString('en-IN')} rows of this sheet could be
                  read. Put the rest in another file or sheet and import it afterwards.
                </Banner>
              )}
              <div className="mt-4 flex justify-end">
                <Button onClick={() => setStep('map')} icon={<ArrowRight className="size-4" />}>
                  Match columns
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {step === 'map' && (
        <Card className="p-0">
          {onlyNames && (
            <Banner tone="warning">
              Only a name column matched: no price, stock, barcode or category. If this is a list of
              categories, suppliers or units rather than products, go back and choose another file.
            </Banner>
          )}
          <div className="border-b border-border p-4 text-sm text-muted">
            We matched what we could. Check each column, pick <b>Don’t import</b> for columns you
            don’t need. This matching is remembered for sheets with the same columns.
          </div>
          {/* One row per column; on phones each row stacks: name, examples, then the choice. */}
          <table className="block w-full text-sm sm:table sm:border-collapse">
            <thead className="hidden sm:table-header-group">
              <tr>
                <TH>Column in your sheet</TH>
                <TH>Example</TH>
                <TH>Import as</TH>
              </tr>
            </thead>
            <tbody className="block sm:table-row-group">
              {Array.from({ length: columnCount }, (_, col) => {
                const field = (Object.entries(mapping) as [MapKey, number][]).find(
                  ([, c]) => c === col,
                )?.[0]
                const examples = dataRows
                  .map((r) => r[col] ?? '')
                  .filter(Boolean)
                  .slice(0, 2)
                return (
                  <tr
                    key={col}
                    className="block border-b border-border px-4 py-3 sm:table-row sm:p-0"
                  >
                    <td className="block font-medium sm:table-cell sm:px-3 sm:py-2.5">
                      {headers[col] || `Column ${col + 1}`}
                    </td>
                    <td className="block truncate text-xs text-muted sm:table-cell sm:max-w-60 sm:px-3 sm:py-2.5 sm:text-sm">
                      <span className="sm:hidden">e.g. </span>
                      {examples.join(' · ') || '—'}
                    </td>
                    <td className="mt-2 block sm:mt-0 sm:table-cell sm:w-64 sm:px-3 sm:py-2.5">
                      <Select
                        aria-label={`Import ${headers[col] || `column ${col + 1}`} as`}
                        value={field ?? ''}
                        onChange={(e) => setColumnField(col, e.target.value as MapKey | '')}
                      >
                        <option value="">Don’t import</option>
                        {allFields.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                            {f.required ? ' *' : ''}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="text-sm">
              {missingRequired.length > 0 ? (
                <span className="text-danger">
                  Choose a column for: {missingRequired.map((f) => f.label).join(', ')}
                </span>
              ) : (
                <span className="text-muted">
                  {mapping.sku === undefined && 'No SKU column: codes are made from the names. '}
                  {mapping.sell_price === undefined &&
                    mapping.mrp !== undefined &&
                    'MRP is used as the selling price. '}
                  {mapping.sell_price === undefined && 'No price column: products start at 0. '}
                  {mapping.opening_stock === undefined && 'No stock column: products start at 0.'}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setStep('data')}>
                Back
              </Button>
              <Button
                disabled={missingRequired.length > 0}
                onClick={toReview}
                icon={<ArrowRight className="size-4" />}
              >
                Review
              </Button>
            </div>
          </div>
        </Card>
      )}

      {step === 'review' && (
        <div className="space-y-4">
          {!plan ? (
            <Card className="p-0">
              <SkeletonTable rows={6} cols={5} label="Checking rows" />
            </Card>
          ) : (
            <>
              {parts > 1 && (
                <Card className="flex flex-wrap items-end justify-between gap-3">
                  <div className="text-sm">
                    <p className="font-semibold">
                      Part {part + 1} of {parts}
                    </p>
                    <p className="text-muted">
                      Rows {rowNumbers[0]?.toLocaleString('en-IN')}–
                      {rowNumbers.at(-1)?.toLocaleString('en-IN')} of the sheet. Parts can be
                      imported in any order, and again: products already in are recognised.
                    </p>
                  </div>
                  <Field label="Part" className="w-56">
                    {(id) => (
                      <Select
                        id={id}
                        value={part}
                        disabled={busy}
                        onChange={(e) => goToPart(Number(e.target.value))}
                      >
                        {Array.from({ length: parts }, (_, i) => {
                          const r = partRange(i, totalDataRows)
                          return (
                            <option key={i} value={i}>
                              Part {i + 1}: rows {(headerRow + 2 + r.start).toLocaleString('en-IN')}
                              –{(headerRow + 1 + r.end).toLocaleString('en-IN')}
                            </option>
                          )
                        })}
                      </Select>
                    )}
                  </Field>
                </Card>
              )}

              <Card>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Field label="Existing products">
                    {(id) => (
                      <Select
                        id={id}
                        value={options.onExisting}
                        onChange={(e) =>
                          setOptionOverrides((o) => ({
                            ...o,
                            onExisting: e.target.value as ImportOptions['onExisting'],
                          }))
                        }
                      >
                        <option value="skip">Leave them as they are</option>
                        <option value="update" disabled={!canUpdate}>
                          Update details and prices{canUpdate ? '' : ' (managers)'}
                        </option>
                      </Select>
                    )}
                  </Field>
                  {mapping.vat_rate === undefined && (
                    <Field label="VAT for these products">
                      {(id) => (
                        <Select
                          id={id}
                          value={options.defaultTaxRateId ?? ''}
                          onChange={(e) =>
                            setOptionOverrides((o) => ({
                              ...o,
                              defaultTaxRateId: e.target.value || null,
                            }))
                          }
                        >
                          <option value="">No VAT</option>
                          {lookups.taxRates
                            .filter((t) => t.is_active)
                            .map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                        </Select>
                      )}
                    </Field>
                  )}
                  {mapping.opening_stock !== undefined && (
                    <Field label="Opening stock goes to">
                      {(id) => (
                        <LocationSelect
                          id={id}
                          locations={lookups.activeLocations}
                          value={locationId}
                          onChange={setLocationOverride}
                        />
                      )}
                    </Field>
                  )}
                </div>
              </Card>

              {mapping.price_includes_vat === undefined && (
                <VatChoice
                  includes={options.pricesIncludeVat}
                  onChange={(v) => setOptionOverrides((o) => ({ ...o, pricesIncludeVat: v }))}
                  example={hints?.example ?? null}
                  rateBp={(id) => (id ? (lookups.taxRate(id)?.rate_bp ?? 0) : 0)}
                  settings={settings}
                />
              )}

              {hints && hints.mrpEqualPrice > 0 && (
                <Banner
                  tone="warning"
                  action={
                    mapping.price_includes_vat === undefined ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          setOptionOverrides((o) => ({ ...o, pricesIncludeVat: true }))
                        }
                      >
                        Prices include VAT
                      </Button>
                    ) : undefined
                  }
                >
                  {hints.mrpEqualPrice} product{hints.mrpEqualPrice === 1 ? ' is' : 's are'} priced
                  at {hints.mrpEqualPrice === 1 ? 'its' : 'their'} MRP. MRP always includes VAT, so
                  {hints.mrpEqualPrice === 1
                    ? ' this price probably includes'
                    : ' these prices probably include'}{' '}
                  VAT too
                  {mapping.price_includes_vat === undefined
                    ? '.'
                    : ': check the VAT-included column.'}
                </Banner>
              )}

              {plan.overLimitBy > 0 && !limitToAllowed && (
                <Banner
                  tone="warning"
                  action={
                    productsRemaining ? (
                      <Button size="sm" variant="secondary" onClick={() => setLimitToAllowed(true)}>
                        Import the first {productsRemaining}
                      </Button>
                    ) : undefined
                  }
                >
                  Your plan allows {productsRemaining} more product
                  {productsRemaining === 1 ? '' : 's'}, and this file adds {plan.counts.create}.
                  Upgrade in Settings → Subscription, or import part of the file.
                </Banner>
              )}

              {plan.newUnits.length > 0 && (
                <Card>
                  <p className="mb-1 text-sm font-semibold">New units</p>
                  <p className="mb-3 text-xs text-muted">
                    Your sheet uses units this business doesn’t have yet. Ticked units are added
                    (you can edit them later in Settings → Units).
                  </p>
                  <ul className="divide-y divide-border rounded-xl border border-border">
                    {plan.newUnits.map((u) => {
                      const set = (change: Partial<{ create: boolean; allowsDecimal: boolean }>) =>
                        setUnitChoices((c) => ({
                          ...c,
                          [u.key]: { create: u.create, allowsDecimal: u.allowsDecimal, ...change },
                        }))
                      return (
                        <li
                          key={u.key}
                          className="flex flex-wrap items-center justify-between gap-3 px-3 py-2"
                        >
                          <Checkbox
                            label={
                              <span>
                                Create <b>{u.name}</b> ({u.symbol})
                                <span className="ml-2 text-xs text-muted">
                                  {u.rowCount} row{u.rowCount === 1 ? '' : 's'}
                                  {u.spellings.some((sp) => sp.toLowerCase() !== u.symbol)
                                    ? ` · written as ${u.spellings.join(', ')}`
                                    : ''}
                                </span>
                              </span>
                            }
                            checked={u.create}
                            onChange={(e) => set({ create: e.target.checked })}
                          />
                          <Checkbox
                            label="Allows decimals"
                            checked={u.allowsDecimal}
                            disabled={!u.create}
                            onChange={(e) => set({ allowsDecimal: e.target.checked })}
                          />
                        </li>
                      )
                    })}
                  </ul>
                </Card>
              )}

              {plan.newBrands.length > 0 && (
                <Card>
                  <p className="mb-2 text-sm font-semibold">New brands</p>
                  <div className="flex flex-wrap gap-x-5 gap-y-2">
                    {plan.newBrands.map((b) => {
                      const key = b.trim().toLowerCase()
                      return (
                        <Checkbox
                          key={key}
                          label={`Create brand “${b}”`}
                          checked={!skippedBrands.has(key)}
                          onChange={(e) =>
                            setSkippedBrands((s) => {
                              const next = new Set(s)
                              if (e.target.checked) next.delete(key)
                              else next.add(key)
                              return next
                            })
                          }
                        />
                      )
                    })}
                  </div>
                  <p className="mt-2 text-xs text-muted">
                    Unticked brands are left empty on those products.
                  </p>
                </Card>
              )}

              {plan.newCategories.length > 0 && (
                <Card>
                  <p className="mb-2 text-sm font-semibold">New categories</p>
                  <div className="flex flex-wrap gap-x-5 gap-y-2">
                    {plan.newCategories.map((c) => {
                      const key = c.trim().toLowerCase()
                      return (
                        <Checkbox
                          key={key}
                          label={`Create “${c}”`}
                          checked={!skippedCategories.has(key)}
                          onChange={(e) =>
                            setSkippedCategories((s) => {
                              const next = new Set(s)
                              if (e.target.checked) next.delete(key)
                              else next.add(key)
                              return next
                            })
                          }
                        />
                      )
                    })}
                  </div>
                  <p className="mt-2 text-xs text-muted">
                    Unticked categories are left empty on those products.
                  </p>
                </Card>
              )}

              <ReviewTable
                rows={plan.rows}
                counts={plan.counts}
                filter={filter}
                onFilter={(f) => {
                  setFilter(f)
                  setOffset(0)
                }}
                offset={offset}
                onOffset={setOffset}
                cellValue={(row, field) => {
                  const col = mapping[field]
                  return col === undefined ? '' : (dataRows[row.index]?.[col] ?? '')
                }}
                onEdit={(row, field, value) => {
                  const col = mapping[field]
                  if (col === undefined) return
                  setEdits((e) => ({ ...e, [`${row.index}:${col}`]: value }))
                }}
                labelOf={(f) => fieldLabel(f, extraFields)}
                settings={settings}
              />

              <Card className="space-y-3">
                {progress && <ImportProgress done={progress.done} total={progress.total} />}
                {stopped && !busy && (
                  <Banner tone={stopped.error ? 'danger' : 'warning'}>
                    {stopped.error ? `Import stopped: ${stopped.error}. ` : 'Import stopped. '}
                    {stopped.saved.toLocaleString('en-IN')} of{' '}
                    {stopped.total.toLocaleString('en-IN')} products were saved and are kept.{' '}
                    {plan.counts.create + plan.counts.update > 0
                      ? 'Import again to carry on with the rest.'
                      : 'Nothing is left to import.'}
                  </Banner>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm text-muted">
                    {leftOutCount(plan) > 0 && (
                      <>
                        {leftOutText(plan)}{' '}
                        <button
                          type="button"
                          className="font-medium text-primary hover:underline"
                          onClick={() =>
                            void downloadRows(notImportedRows(headers, dataRows, plan))
                          }
                        >
                          Download them
                        </button>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {busy ? (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          stopRequested.current = true
                        }}
                      >
                        Stop
                      </Button>
                    ) : (
                      <Button variant="secondary" onClick={() => setStep('map')}>
                        Back
                      </Button>
                    )}
                    <Button
                      loading={busy}
                      disabled={
                        plan.counts.create + plan.counts.update === 0 ||
                        (plan.overLimitBy > 0 && !limitToAllowed)
                      }
                      onClick={() => void runImport()}
                    >
                      {importLabel(plan.counts.create, plan.counts.update)}
                    </Button>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {step === 'done' && result && (
        <Card className="text-center">
          <CheckCircle2 className="mx-auto size-10 text-success" />
          <h2 className="mt-3 text-lg font-semibold">
            {parts > 1 ? `Part ${part + 1} of ${parts} finished` : 'Import finished'}
          </h2>
          <p className="mt-1 text-muted">
            {result.created} added · {result.updated} updated · {result.skipped} skipped
            {result.errors ? ` · ${result.errors} with errors` : ''}
            {result.overLimit ? ` · ${result.overLimit} over the plan limit` : ''}
          </p>
          <p className="mt-2 text-sm text-muted">
            {sync.pending > 0
              ? `Saved on this device. ${sync.pending} change${sync.pending === 1 ? '' : 's'} waiting to sync.`
              : 'Saved and synced.'}
          </p>
          {leftOver && leftOver.length > 1 && (
            <p className="mx-auto mt-2 max-w-xl text-sm text-muted">
              {leftOver.length - 1} row{leftOver.length === 2 ? ' wasn’t' : 's weren’t'} imported.
              Download them with the reason, fix them (or upgrade your plan) and import that file.
            </p>
          )}
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {leftOver && leftOver.length > 1 && (
              <Button
                variant="secondary"
                icon={<Download className="size-4" />}
                onClick={() => void downloadRows(leftOver)}
              >
                Download rows not imported
              </Button>
            )}
            <Button variant="secondary" onClick={reset}>
              Import another file
            </Button>
            {part + 1 < parts ? (
              <Button icon={<ArrowRight className="size-4" />} onClick={() => goToPart(part + 1)}>
                Import part {part + 2} of {parts}
              </Button>
            ) : source && sheetIndex + 1 < source.sheets.length ? (
              <Button
                icon={<ArrowRight className="size-4" />}
                onClick={() => {
                  selectSheet(source.sheets, sheetIndex + 1)
                  setResult(null)
                  setLeftOver(null)
                  setStep('map')
                }}
              >
                Import sheet “{source.sheets[sheetIndex + 1]!.name}”
              </Button>
            ) : (
              <Button onClick={() => navigate('/products')}>Go to products</Button>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}

function leftOutCount(plan: ImportPlan): number {
  return plan.counts.error + plan.rows.filter((r) => r.overLimit).length
}

function leftOutText(plan: ImportPlan): string {
  const over = plan.rows.filter((r) => r.overLimit).length
  const n = (value: number) => value.toLocaleString('en-IN')
  const reasons = []
  if (plan.counts.error) reasons.push(`${n(plan.counts.error)} with errors`)
  if (over) reasons.push(`${n(over)} over the plan limit`)
  const total = plan.counts.error + over
  return `${n(total)} row${total === 1 ? '' : 's'} won’t be imported (${reasons.join(', ')}).`
}

function ImportProgress({ done, total }: { done: number; total: number }) {
  const percent = total ? Math.round((done / total) * 100) : 0
  return (
    <div>
      <div className="mb-1 flex justify-between text-sm">
        <span className="font-medium">
          Saving {done.toLocaleString('en-IN')} of {total.toLocaleString('en-IN')} products…
        </span>
        <span className="tabular text-muted">{percent}%</span>
      </div>
      <div
        role="progressbar"
        aria-label="Import progress"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        className="h-2 overflow-hidden rounded-full bg-soft"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width]"
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="mt-1 text-xs text-muted">
        Stopping keeps what’s saved; import again later to carry on.
      </p>
    </div>
  )
}

/** "Are the prices in this sheet with VAT?", with the sheet's own first product as the example. */
function VatChoice({
  includes,
  onChange,
  example,
  rateBp,
  settings,
}: {
  includes: boolean
  onChange: (includes: boolean) => void
  example: VatHints['example']
  rateBp: (taxRateId: string | null) => number
  settings: Parameters<typeof money>[1]
}) {
  const bp = example ? rateBp(example.taxRateId) : 0
  const pays = (withVat: boolean) =>
    example && bp > 0
      ? `${example.name} at ${money(example.pricePaisa, settings)} → customer pays ${money(customerPays(example.pricePaisa, bp, withVat), settings)}`
      : null
  const options = [
    { value: true, label: 'Prices include VAT', example: pays(true) },
    { value: false, label: 'Prices are before VAT (VAT is added on top)', example: pays(false) },
  ]
  return (
    <Card>
      <fieldset>
        <legend className="mb-2 text-sm font-semibold">
          Do the prices in this sheet include VAT?
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {options.map((o) => (
            <label
              key={String(o.value)}
              className={cx(
                'flex cursor-pointer gap-3 rounded-xl border p-3 text-sm',
                includes === o.value ? 'border-primary bg-primary-light/40' : 'border-border',
              )}
            >
              <input
                type="radio"
                name="prices-include-vat"
                className="mt-0.5 size-4 accent-primary"
                checked={includes === o.value}
                onChange={() => onChange(o.value)}
              />
              <span>
                <span className="font-medium">{o.label}</span>
                {o.example && <span className="mt-0.5 block text-xs text-muted">{o.example}</span>}
              </span>
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted">
          Imported with the wrong choice before? Import the file again with “Update details and
          prices”, or change many products at once on the Products screen (Select).
        </p>
      </fieldset>
    </Card>
  )
}

function importLabel(create: number, update: number): string {
  const parts = []
  if (create) parts.push(`add ${create}`)
  if (update) parts.push(`update ${update}`)
  if (parts.length === 0) return 'Nothing to import'
  const text = parts.join(', ')
  return `${text.charAt(0).toUpperCase()}${text.slice(1)} product${create + update === 1 ? '' : 's'}`
}

function Stepper({ step }: { step: Step }) {
  const active = STEPS.findIndex((s) => s.key === step)
  return (
    <>
      {/* Phones: one line and a bar instead of four wrapping steps. */}
      <div className="mb-4 sm:hidden">
        <p className="text-sm">
          <span className="text-muted">
            Step {active + 1} of {STEPS.length}:
          </span>{' '}
          <span className="font-semibold">{STEPS[active]!.label}</span>
        </p>
        <div className="mt-2 grid grid-cols-4 gap-1" aria-hidden>
          {STEPS.map((s, i) => (
            <span
              key={s.key}
              className={cx('h-1 rounded-full', i <= active ? 'bg-primary' : 'bg-soft')}
            />
          ))}
        </div>
      </div>
      <ol
        className="mb-5 hidden flex-wrap items-center gap-2 text-sm sm:flex"
        aria-label="Import steps"
      >
        {STEPS.map((s, i) => (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={cx(
                'flex size-6 items-center justify-center rounded-full text-xs font-bold',
                i < active && 'bg-success text-on-fill',
                i === active && 'bg-primary text-on-fill',
                i > active && 'bg-soft text-muted',
              )}
              aria-current={i === active ? 'step' : undefined}
            >
              {i + 1}
            </span>
            <span className={cx(i === active ? 'font-semibold text-ink' : 'text-muted')}>
              {s.label}
            </span>
            {i < STEPS.length - 1 && <span className="mx-1 h-px w-6 bg-border" />}
          </li>
        ))}
      </ol>
    </>
  )
}

const STATUS: Record<
  PlannedRow['status'],
  { label: string; tone: 'success' | 'primary' | 'neutral' | 'danger' }
> = {
  create: { label: 'New', tone: 'success' },
  update: { label: 'Update', tone: 'primary' },
  skip: { label: 'Skip', tone: 'neutral' },
  error: { label: 'Error', tone: 'danger' },
}

function ReviewTable({
  rows,
  counts,
  filter,
  onFilter,
  offset,
  onOffset,
  cellValue,
  onEdit,
  labelOf,
  settings,
}: {
  rows: PlannedRow[]
  counts: Record<PlannedRow['status'], number>
  filter: Filter
  onFilter: (f: Filter) => void
  offset: number
  onOffset: (o: number) => void
  cellValue: (row: PlannedRow, field: MapKey) => string
  onEdit: (row: PlannedRow, field: MapKey, value: string) => void
  labelOf: (field: MapKey) => string
  settings: Parameters<typeof money>[1]
}) {
  const shown = filter === 'all' ? rows : rows.filter((r) => r.status === filter)
  const page = shown.slice(offset, offset + PAGE)
  const tabs: { key: Filter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: rows.length },
    { key: 'error', label: 'Errors', count: counts.error },
    { key: 'create', label: 'New', count: counts.create },
    { key: 'update', label: 'Updates', count: counts.update },
    { key: 'skip', label: 'Skipped', count: counts.skip },
  ]
  return (
    <Card className="p-0">
      <div
        className="flex flex-wrap gap-2 border-b border-border p-3"
        role="tablist"
        aria-label="Filter rows"
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={filter === t.key}
            onClick={() => onFilter(t.key)}
            className={cx(
              'rounded-xl px-3 py-1.5 text-sm font-medium',
              filter === t.key ? 'bg-primary-light text-primary' : 'text-muted hover:bg-soft',
            )}
          >
            {t.label} <span className="tabular">{t.count}</span>
          </button>
        ))}
      </div>
      {page.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted">No rows here.</p>
      ) : (
        // A table from sm up; on phones each row is a small card:
        // status, product and row number, then price and stock, then the details.
        <table className="block w-full text-sm sm:table sm:border-collapse">
          <thead className="hidden sm:table-header-group">
            <tr>
              <TH>Row</TH>
              <TH>Status</TH>
              <TH>Product</TH>
              <TH align="right">Price</TH>
              <TH align="right">Stock</TH>
              <TH>Details</TH>
            </tr>
          </thead>
          <tbody className="block sm:table-row-group">
            {page.map((r) => {
              const stock =
                r.values?.openingMilli != null && r.status === 'create'
                  ? qty(r.values.openingMilli)
                  : null
              return (
                <tr
                  key={r.index}
                  className={cx(
                    'grid grid-cols-[auto_1fr_auto] items-start gap-x-3 gap-y-1 border-b border-border px-4 py-3 sm:table-row sm:p-0',
                    r.status === 'error' && 'bg-danger-light/30',
                  )}
                >
                  <td className="order-3 text-xs text-muted sm:order-none sm:table-cell sm:px-3 sm:py-2.5 sm:text-sm">
                    <span className="sm:hidden">Row </span>
                    {r.rowNumber}
                  </td>
                  <td className="order-1 sm:order-none sm:table-cell sm:px-3 sm:py-2.5">
                    <Badge tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Badge>
                  </td>
                  <td className="order-2 min-w-0 sm:order-none sm:table-cell sm:px-3 sm:py-2.5">
                    <div className="font-medium">
                      {r.values?.name ?? (cellValue(r, 'name') || '—')}
                    </div>
                    <div className="text-xs text-muted">
                      {r.values?.sku ?? cellValue(r, 'sku')}
                      {r.values?.skuGenerated ? ' (made from name)' : ''}
                    </div>
                  </td>
                  <td
                    className={cx(
                      'order-4 col-span-3 text-xs sm:order-none sm:table-cell sm:px-3 sm:py-2.5 sm:text-right sm:text-sm tabular',
                      !r.values && 'max-sm:hidden',
                    )}
                  >
                    <span className="text-muted sm:hidden">Price </span>
                    {r.values ? money(r.values.sellPricePaisa, settings) : '—'}
                    {stock && (
                      <span className="text-muted sm:hidden"> · Opening stock {stock}</span>
                    )}
                  </td>
                  <td className="hidden tabular sm:table-cell sm:px-3 sm:py-2.5 sm:text-right">
                    {stock ?? '—'}
                  </td>
                  <td
                    className={cx(
                      'order-5 col-span-3 sm:order-none sm:table-cell sm:min-w-72 sm:px-3 sm:py-2.5',
                      r.errors.length + (r.diff?.length ?? 0) + r.notes.length === 0 &&
                        'max-sm:hidden',
                    )}
                  >
                    {r.errors.map((e) => (
                      <ErrorCell
                        key={`${e.field}-${e.message}`}
                        label={labelOf(e.field)}
                        message={e.message}
                        value={cellValue(r, e.field)}
                        onSave={(v) => onEdit(r, e.field, v)}
                      />
                    ))}
                    {r.diff?.map((d) => (
                      <div key={d.field} className="text-xs">
                        {d.label}: <span className="text-muted line-through">{d.from}</span> →{' '}
                        <span className="font-medium">{d.to}</span>
                      </div>
                    ))}
                    {r.notes.map((n) => (
                      <div key={n} className="text-xs text-muted">
                        {n}
                      </div>
                    ))}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
      <div className="p-3">
        <Pagination offset={offset} limit={PAGE} total={shown.length} onChange={onOffset} />
      </div>
    </Card>
  )
}

function ErrorCell({
  label,
  message,
  value,
  onSave,
}: {
  label: string
  message: string
  value: string
  onSave: (value: string) => void
}) {
  const [draft, setDraft] = useState(value)
  return (
    <div className="mb-1.5">
      <p className="text-xs text-danger">{message}</p>
      <Input
        aria-label={`Fix ${label}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => draft !== value && onSave(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        }}
        className="mt-0.5 h-8 max-w-56 text-sm"
      />
    </div>
  )
}
